import { Accordion } from "@components/Accordion";
import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "@components/Button";
import { Card } from "@components/Card";
import { ErrorMessage } from "@components/ErrorMessage";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import {
  SearchSelectorInput,
  SelectableAutocompleteResult,
} from "@components/SearchSelectorInput";
import { AutocompleteSection } from "@components/SearchTextInput";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useCallback, useEffect, useState } from "react";

import { colorPalette } from "@every.org/common/src/display/palette";
import { SPECIAL_FUNDRAISER_NONPROFIT_SLUG } from "@every.org/common/src/entity/constants";
import { FUNDRAISER_MAX_NONPROFITS } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import { Modal } from "src/components/Modal";
import { useNonprofit } from "src/context/NonprofitsContext/hooks";
import { nonprofitOrUndefined } from "src/context/NonprofitsContext/selectors";
import { FundraiserNonprofitsList } from "src/pages/Fundraiser/FundraiserNonprofitsList";
import {
  horizontalStackCss,
  spacing,
  verticalStackCss,
} from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";

const StyledAlertCard = styled(Card)`
  ${verticalStackCss.xxs};
  align-items: flex-start;
  max-width: 600px;
  padding: ${spacing.xs};
  border-radius: 8px;
  background-color: rgba(43, 215, 176, 0.15);
  p {
    ${textSizeCss.xs};
  }
  margin-top: ${spacing.l};
  padding: ${spacing.m};
`;

const AccordionWrapper = styled.div`
  margin-top: ${spacing.m};
  & .AccordionStyledHeader {
    justify-content: flex-start;
  }
`;

const searchSelectorDropDownCss = css`
  position: relative;
  top: ${spacing.xs};
`;

function concatNonprofits(
  selectedNonprofitIds: string[],
  selectedFromSearchNonprofits: SelectableAutocompleteResult[]
) {
  return [
    ...new Set(
      selectedNonprofitIds.concat(
        selectedFromSearchNonprofits.map(({ id }) => id)
      )
    ),
  ];
}

export const CreateMultiNonprofitFundraiserModal: React.FCC<{
  isModalOpen?: boolean;
  onRequestClose: () => void;
  onEdit?: (ids: string[]) => void;
  forNonprofits?: string[];
}> = ({ isModalOpen = false, onRequestClose, onEdit, forNonprofits }) => {
  const [error, setError] = useState<string | undefined>(undefined);

  const [selectedFromSearchNonprofits, setSelectedFromSearchNonprofits] =
    useState<SelectableAutocompleteResult[]>([]);

  const [selectedNonprofitIds, setSelectedNonprofitIds] = useState<string[]>(
    forNonprofits ?? []
  );

  const newNonprofits = concatNonprofits(
    selectedNonprofitIds,
    selectedFromSearchNonprofits
  );

  const onRemove = (id: string) => {
    setError(undefined);
    setSelectedFromSearchNonprofits((prev) =>
      prev.filter(({ id: nonprofitId }) => nonprofitId !== id)
    );
    setSelectedNonprofitIds((prev) =>
      prev.filter((nonprofitId) => nonprofitId !== id)
    );
  };

  const onAdd = (nonprofits: SelectableAutocompleteResult[]) => {
    setError(undefined);
    setSelectedFromSearchNonprofits(nonprofits);
  };

  const onEditConfirm = () => {
    if (!onEdit) {
      return;
    }

    if (newNonprofits.length < 2) {
      setError("You need to select at least 2 nonprofits");
      return;
    }

    if (newNonprofits.length > FUNDRAISER_MAX_NONPROFITS) {
      setError(
        `You can only select up to ${FUNDRAISER_MAX_NONPROFITS} nonprofits`
      );
      return;
    }

    onEdit(newNonprofits);
    setSelectedNonprofitIds(newNonprofits);
    setSelectedFromSearchNonprofits([]);
    onRequestClose();
  };

  useEffect(() => {
    // If the modal is closed, reset the selected nonprofits
    setSelectedNonprofitIds(forNonprofits ?? []);
    setSelectedFromSearchNonprofits([]);

    return () => {
      setSelectedNonprofitIds(forNonprofits ?? []);
      setSelectedFromSearchNonprofits([]);
    };
  }, [forNonprofits, isModalOpen]);

  const title = onEdit
    ? "Edit fundraiser nonprofits"
    : "Start a multi-nonprofit fundraiser";

  const description = onEdit
    ? `Add or remove nonprofits for your fundraiser (up to ${FUNDRAISER_MAX_NONPROFITS} max)`
    : `Select up to ${FUNDRAISER_MAX_NONPROFITS} nonprofits to be included
in this fundraiser`;

  return (
    <Modal isOpen={isModalOpen} onRequestClose={onRequestClose}>
      <div css={horizontalStackCss.xs}>
        <div>
          <Icon
            iconImport={() =>
              import("@components/Icon/icons/ThermometerStarsIcon")
            }
            size={IconSize.XX_LARGE}
            display={IconDisplay.SECONDARY}
          />
        </div>
        <div>
          <h4 css={{ marginBottom: spacing.xxs }}>{title}</h4>
          <p css={{ color: colorPalette.grayMedium }}>{description}</p>
        </div>
        <Button
          aria-label="close modal"
          data-tname="closeNewMultiNpFundraiserModal"
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: onRequestClose,
          }}
          css={{
            marginLeft: "auto",
            alignSelf: "flex-start",
            paddingLeft: spacing.xs,
          }}
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.SMALL}
        >
          <Icon
            iconImport={() => import("@components/Icon/icons/XIcon")}
            size={IconSize.MEDIUM}
            display={IconDisplay.CURRENT_COLOR}
          />
        </Button>
      </div>
      <section>
        <div
          css={{
            marginTop: spacing.l,
            maxWidth: "600px",
            minWidth: "200px",
            minHeight: "200px",
            position: "relative",
          }}
        >
          <h4 css={{ marginBottom: spacing.s }}>Add nonprofits</h4>
          <SearchSelectorInput
            selector={AutocompleteSection.NONPROFITS}
            selectedSuggestions={selectedFromSearchNonprofits}
            setSelectedSuggestions={onAdd}
            data-tname="searchNonprofits"
            selectionLimit={FUNDRAISER_MAX_NONPROFITS}
            dropDownCss={searchSelectorDropDownCss}
            queryOptions={{ donationsEnabled: true, hasAdmin: true }}
            alwaysOpen
          />
          <AccordionWrapper>
            <Accordion
              dataList={[
                {
                  id: "searchHelp",
                  title: "Can't find a nonprofit?",
                  description:
                    "Try searching by your organization's EIN. If you know the nonprofit has gone through a name change, the profile might be under the older name.",
                },
              ]}
            />
          </AccordionWrapper>
          {onEdit ? (
            <div css={{ marginTop: spacing.l }}>
              <FundraiserNonprofitsList
                nonprofitIds={newNonprofits}
                onRemove={onRemove}
              />
            </div>
          ) : (
            <StyledAlertCard>
              <div css={horizontalStackCss.s}>
                <Icon
                  iconImport={() =>
                    import("@components/Icon/icons/ThumbsUpIcon")
                  }
                  size={IconSize.MEDIUM}
                  display={IconDisplay.ACCENT}
                />
                <h4>Equal distribution</h4>
              </div>
              <p css={{ marginTop: spacing.xs }}>
                When multiple nonprofits are selected, the funds raised from
                your fundraiser will be distributed evenly among the selected
                nonprofit organizations.
              </p>
            </StyledAlertCard>
          )}
          {error && (
            <ErrorMessage css={{ marginTop: spacing.s }}>{error}</ErrorMessage>
          )}
          <div
            css={[
              horizontalStackCss.l,
              { marginTop: spacing.l, justifyContent: "flex-end" },
            ]}
          >
            {onEdit ? (
              <React.Fragment>
                <Button
                  data-tname="multi-np-fundraiser-edit-revert"
                  role={ButtonRole.TEXT_SECONDARY}
                  onClick={{
                    kind: ButtonTargetKind.FUNCTION,
                    action: () => {
                      setSelectedNonprofitIds(forNonprofits ?? []);
                      setSelectedFromSearchNonprofits([]);
                    },
                  }}
                >
                  Revert changes
                </Button>
                <Button
                  data-tname="multi-np-fundraiser-edit-confirm"
                  role={ButtonRole.PRIMARY}
                  disabled={newNonprofits?.length === 0}
                  onClick={{
                    kind: ButtonTargetKind.FUNCTION,
                    action: onEditConfirm,
                  }}
                >
                  Confirm
                </Button>
              </React.Fragment>
            ) : (
              <Button
                data-tname="multi-np-fundraiser-create"
                role={ButtonRole.PRIMARY}
                disabled={selectedFromSearchNonprofits?.length === 0}
                onClick={{
                  kind: ButtonTargetKind.LINK,
                  to: getRoutePath({
                    name: ClientRouteName.FUNDRAISER_CREATE,
                    tokens: {
                      nonprofitSlug: SPECIAL_FUNDRAISER_NONPROFIT_SLUG,
                    },
                    ...(selectedFromSearchNonprofits &&
                    selectedFromSearchNonprofits.length > 0
                      ? {
                          query: {
                            forNonprofits: selectedFromSearchNonprofits
                              .map(({ id }) => id)
                              .join(","),
                          },
                        }
                      : {}),
                    format: URLFormat.RELATIVE,
                  }),
                }}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </section>
    </Modal>
  );
};

export const CreateSingleNonprofitFundraiserModal: React.FCC<{
  isModalOpen: boolean;
  onRequestClose: () => void;
}> = ({ isModalOpen, onRequestClose }) => {
  const [selectedNonprofitId, setSelectedNonprofitId] = useState<
    string | undefined
  >(undefined);

  const onSelectedChange = useCallback(
    (ids?: string[] | undefined) =>
      setSelectedNonprofitId(ids ? ids[0] : undefined),
    []
  );

  const selectedNonprofit = nonprofitOrUndefined(
    useNonprofit(
      selectedNonprofitId ? { slug: selectedNonprofitId } : undefined
    )
  );

  return (
    <Modal isOpen={isModalOpen} onRequestClose={onRequestClose}>
      <div css={horizontalStackCss.xs}>
        <div>
          <Icon
            iconImport={() =>
              import("@components/Icon/icons/ThermometerStarsIcon")
            }
            size={IconSize.XX_LARGE}
            display={IconDisplay.SECONDARY}
          />
        </div>
        <div>
          <h4 css={{ marginBottom: spacing.xxs }}>Start a fundraiser</h4>
          <p css={{ color: colorPalette.grayMedium }}>
            Select the nonprofit you want to support with your fundraiser.
          </p>
        </div>
        <Button
          aria-label="close modal"
          data-tname="closeNewSingleNonprofitFundraiserModal"
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: onRequestClose,
          }}
          css={{
            marginLeft: "auto",
            alignSelf: "flex-start",
            paddingLeft: spacing.xs,
          }}
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.SMALL}
        >
          <Icon
            iconImport={() => import("@components/Icon/icons/XIcon")}
            size={IconSize.MEDIUM}
            display={IconDisplay.CURRENT_COLOR}
          />
        </Button>
      </div>
      <section>
        <div
          css={{
            marginTop: spacing.l,
            maxWidth: "600px",
            minWidth: "200px",
            minHeight: "200px",
            position: "relative",
          }}
        >
          <SearchSelectorInput
            selector={AutocompleteSection.NONPROFITS}
            onSelectedChange={onSelectedChange}
            multiSelect={false}
            css={css`
              width: 100%;
            `}
            dropDownCss={searchSelectorDropDownCss}
            placeholder={"Search by your organization's EIN or name"}
            data-tname="searchSelector"
            queryOptions={{ donationsEnabled: true, hasAdmin: true }}
            alwaysOpen
          />
          <AccordionWrapper>
            <Accordion
              dataList={[
                {
                  id: "searchHelp",
                  title: "Can't find a nonprofit?",
                  description:
                    "Try searching by your organization's EIN. If you know the nonprofit has gone through a name change, the profile might be under the older name.",
                },
              ]}
            />
          </AccordionWrapper>
          <div
            css={[
              horizontalStackCss.l,
              { marginTop: spacing.l, justifyContent: "flex-end" },
            ]}
          >
            <Button
              data-tname="single-fundraiser-create"
              role={ButtonRole.PRIMARY}
              disabled={!selectedNonprofit}
              onClick={{
                kind: ButtonTargetKind.LINK,
                to: getRoutePath({
                  name: ClientRouteName.FUNDRAISER_CREATE,
                  tokens: {
                    nonprofitSlug: selectedNonprofit?.primarySlug || "UNKNOWN",
                  },
                  format: URLFormat.RELATIVE,
                }),
              }}
            >
              Next
            </Button>
          </div>
        </div>
      </section>
    </Modal>
  );
};
